/**
 * 存储服务工厂
 * 管理存储服务实例的创建和访问
 *
 * 核心功能：
 * 1. 管理DocumentStorageService实例
 * 2. 管理TranslationStorageService实例
 * 3. 提供统一的访问接口
 * 4. 支持依赖注入模式
 *
 * 未来扩展计划：
 * 1. 支持更多存储服务类型
 * 2. 支持配置化的服务实例创建
 * 3. 支持运行时服务实现切换
 */
import { DocumentStorageService } from './services/DocumentStorageService.js';
import { PdfStorageService } from './services/PdfStorageService.js';
import { TranslationStorageService } from './services/TranslationStorageService.js';
import { LoggerService } from '../logging/services/logger.service.js';
import { registerDocumentStorageService, registerPdfStorageService, registerTranslationStorageService, } from './contexts/StorageContext.js';
/**
 * 存储服务工厂类
 * 集中管理存储服务实例的创建和访问
 */
export class StorageFactory {
    /**
     * 获取文档存储服务实例
     * @returns 文档存储服务实例
     */
    static getDocumentStorage() {
        if (!this.documentStorage) {
            try {
                this.documentStorage = DocumentStorageService.getInstance();
                // 检查初始化状态
                if (this.documentStorage) {
                    // 这里可以添加检查初始化状态的代码
                }
                // 自动注册到上下文，便于依赖注入
                try {
                    registerDocumentStorageService(this.documentStorage);
                }
                catch (error) {
                    // Comment and logger.warn removed
                }
            }
            catch (error) {
                this.logger.error('[StorageFactory] Failed to create DocumentStorageService instance', {
                    error,
                });
                throw new Error('Failed to create DocumentStorageService: ' + String(error));
            }
        }
        return this.documentStorage;
    }
    /**
     * 获取PDF文档存储服务实例
     * @returns PDF文档存储服务实例
     */
    static getPdfStorage() {
        if (!this.pdfStorage) {
            try {
                this.pdfStorage = PdfStorageService.getInstance();
                registerPdfStorageService(this.pdfStorage);
            }
            catch (error) {
                this.logger.error('[StorageFactory] Failed to create PdfStorageService instance', {
                    error,
                });
                throw new Error('Failed to create PdfStorageService: ' + String(error));
            }
        }
        return this.pdfStorage;
    }
    /**
     * 获取翻译存储服务实例
     * @returns 翻译存储服务实例
     */
    static getTranslationStorage() {
        if (!this.translationStorage) {
            try {
                this.translationStorage = TranslationStorageService.getInstance();
                // Ensure instance was created successfully before using it
                if (!this.translationStorage) {
                    // This should ideally not happen with the Singleton pattern, but good for robustness
                    throw new Error('TranslationStorageService instance could not be created.');
                }
                // Auto-register to context
                registerTranslationStorageService(this.translationStorage);
            }
            catch (error) {
                this.logger.error('[StorageFactory] Failed to create or register TranslationStorageService instance', { error });
                // Re-throw or handle as appropriate
                throw new Error('Failed to create/register TranslationStorageService: ' + String(error));
            }
        }
        // Final check before returning to satisfy non-nullable return type
        if (!this.translationStorage) {
            // This path should be unreachable if the above logic is sound
            this.logger.error('[StorageFactory] Invariant violated: translationStorage is null before return');
            throw new Error('Failed to get TranslationStorageService instance.');
        }
        return this.translationStorage;
    }
    /**
     * 根据文档标识获取相应的存储服务
     * @param identity 文档标识
     * @returns 相应的文档存储服务
     */
    static getServiceFor(identity) {
        if (identity.type === 'pdf') {
            return this.getPdfStorage();
        }
        // 默认为文本文档服务
        return this.getDocumentStorage();
    }
    /**
     * 初始化所有存储服务
     * 预创建并注册所有服务实例
     */
    static async initializeAll() {
        const docStorage = this.getDocumentStorage();
        const pdfStorage = this.getPdfStorage();
        const transStorage = this.getTranslationStorage();
        // Await the initialization of all services
        try {
            await Promise.all([
                docStorage.initialize(),
                pdfStorage.initialize(),
                transStorage.initialize(),
            ]);
        }
        catch (error) {
            this.logger.error('[StorageFactory] Failed during storage service initialization', { error });
            // Re-throw the error to be caught by the caller in index.tsx
            throw error;
        }
    }
    /**
     * 重置所有存储服务实例
     * 用于测试或重新配置服务
     */
    static reset() {
        this.documentStorage = null;
        this.pdfStorage = null;
        this.translationStorage = null;
    }
}
StorageFactory.documentStorage = null;
StorageFactory.pdfStorage = null;
StorageFactory.translationStorage = null;
StorageFactory.logger = LoggerService.getInstance();
//# sourceMappingURL=factory.service.js.map